import React from 'react';
import { timeHours } from 'd3-time';
import { DateTime } from 'luxon';
import { Datum, IGraphData } from './types';
import { ScaleLinear, ScaleTime } from 'd3-scale';
import _ from 'lodash';

export const EXTREMES_SCOPE = {
    LOCAL: {
        value: 'LOCAL',
        label: 'Local extremes',
    },
    ABSOLUTE: {
        value: 'ABSOLUTE',
        label: 'Absolute extremes',
    },
    WEEK: {
        value: 'WEEK',
        label: 'Weekly extremes',
    },
    DAY: {
        value: 'DAY',
        label: 'Daily extremes',
    },
    HALFDAY: {
        value: 'HALFDAY',
        label: '12 hours extremes',
    },
    EVERYTHING_MAXIMA: {
        value: 'EVERYTHING_MAXIMA',
        label: 'Everything (as maxima)',
    },
    EVERYTHING_MINIMA: {
        value: 'EVERYTHING_MINIMA',
        label: 'Everything (as minima)',
    },
};

export const getLocalExtremes = (data: Datum[]) => {
    const min = [];
    const max = [];

    const timeArray = Object.values(_.groupBy(data, 'x'));
    const localExtremes = timeArray.map((time: Datum[]) => {
        const max = time.reduce((prev, current) => (prev && prev.y > current.y ? prev : current));
        const min = time.reduce((prev, current) => (prev && prev.y < current.y ? prev : current));
        return { max, min };
    });

    for (let i = 1; i < localExtremes.length - 1; i++) {
        const current = localExtremes[i];
        const prev = localExtremes[i - 1];
        const next = localExtremes[i + 1];
        if (current.min.y < prev.min.y && current.min.y <= next.min.y) {
            min.push(current.min);
        } else if (current.max.y >= prev.max.y && current.max.y > next.max.y) {
            max.push(current.max);
        }
    }

    return {
        min,
        max,
    };
};

export const getAbsoluteExtremes = (data: Datum[]) => {
    if (data.length === 0) {
        return {
            min: [],
            max: [],
        };
    }
    const max = data.reduce((prev, current) => (prev && prev.y > current.y ? prev : current));
    const min = data.reduce((prev, current) => (prev && prev.y < current.y ? prev : current));
    return {
        min: [min],
        max: [max],
    };
};

export const getExtremesByTimeInterval = ({ data, hourStep, timezone }: { data: Datum[]; hourStep: number; timezone: string }) => {
    const firstDataEntryDate = DateTime.fromMillis(data[0].x).setZone(timezone).startOf('day');
    const lastDataEntryDate = DateTime.fromMillis(data[data.length - 1].x)
        .setZone(timezone)
        .endOf('day');

    const hoursInterval = timeHours(firstDataEntryDate.toJSDate(), lastDataEntryDate.plus({ days: 1 }).toJSDate(), hourStep);

    if (hoursInterval.length === 1) {
        return getAbsoluteExtremes(data);
    }

    const dataIntervalsByHourStep = hoursInterval.map((interval, index) => {
        return data.filter(
            (data) => new Date(data.x).getTime() >= new Date(interval).getTime() && new Date(data.x).getTime() < new Date(hoursInterval[index + 1]).getTime()
        );
    });

    const extremesInIntervals = dataIntervalsByHourStep.map(getAbsoluteExtremes);

    return extremesInIntervals.reduce(
        (minMax, current) => {
            return {
                min: [...minMax.min, ...current.min],
                max: [...minMax.max, ...current.max],
            };
        },
        { min: [], max: [] }
    );
};

export const calculateExtremes = ({ scope, data, timezone }: { scope: string; data: Datum[]; timezone: string }) => {
    if (data.length === 0) {
        return {
            min: [],
            max: [],
        };
    }

    switch (scope) {
        case EXTREMES_SCOPE.LOCAL.value:
            return getLocalExtremes(data);
        case EXTREMES_SCOPE.ABSOLUTE.value:
            return getAbsoluteExtremes(data);
        case EXTREMES_SCOPE.WEEK.value:
            return getExtremesByTimeInterval({ data, hourStep: 24 * 7, timezone });
        case EXTREMES_SCOPE.DAY.value:
            return getExtremesByTimeInterval({ data, hourStep: 24, timezone });
        case EXTREMES_SCOPE.HALFDAY.value:
            return getExtremesByTimeInterval({ data, hourStep: 12, timezone });
        case EXTREMES_SCOPE.EVERYTHING_MAXIMA.value:
            return {
                min: [],
                max: Object.values(_.groupBy(data, 'x')).map((time: Datum[]) => time.reduce((prev, current) => (prev && prev.y > current.y ? prev : current))),
            };
        case EXTREMES_SCOPE.EVERYTHING_MINIMA.value:
            return {
                min: Object.values(_.groupBy(data, 'x')).map((time: Datum[]) => time.reduce((prev, current) => (prev && prev.y < current.y ? prev : current))),
                max: [],
            };
        default:
            return {
                min: [],
                max: [],
            };
    }
};

const minimaAndMaximaValue = ({ reference, timeX, y }: { reference: string; timeX?: string; y?: number }) => {
    const hasXAxis = reference.includes('xAxis');
    const hasYAxis = reference.includes('yAxis');
    if (hasXAxis && hasYAxis) {
        return `${y}, ${timeX}`;
    } else if (hasXAxis) {
        return timeX ?? '';
    } else if (hasYAxis) {
        return y ? y.toString() : '';
    }
    return '';
};

const MinimaAndMaxima = ({
    lineData,
    xScale,
    yScale,
}: {
    lineData: IGraphData;
    xScale: ScaleTime<number, number, never>;
    yScale: ScaleLinear<number, number, never>;
}) => {
    return (
        <>
            {lineData.highlightMinima &&
                lineData.min.map(({ x, y, roundedY, timeX }, index) => {
                    const xpos = xScale(x);
                    const ypos = yScale(y);
                    return (
                        <g fill={lineData.minimaColour} key={`minima_${xpos}_${ypos}_${index}`}>
                            <circle cx={xpos} cy={ypos} r='3' fill={lineData.minimaColour} />
                            <text
                                x={xpos}
                                y={ypos + (5 + lineData.lineThickness / 2)}
                                style={{ fontSize: '0.7em' }}
                                dominantBaseline='hanging'
                                textAnchor='middle'
                            >
                                {minimaAndMaximaValue({ reference: lineData.minimaReference, timeX, y: roundedY })}
                            </text>
                        </g>
                    );
                })}
            {lineData.highlightMaxima &&
                lineData.max.map(({ x, y, roundedY, timeX }, index) => {
                    const xpos = xScale(x);
                    const ypos = yScale(y);
                    return (
                        <g fill={lineData.maximaColour} key={`maxima_${xpos}_${ypos}_${index}`}>
                            <circle cx={xpos} cy={ypos} r='3' fill={lineData.maximaColour} />
                            <text
                                x={xpos}
                                y={ypos - (8 + lineData.lineThickness / 2)}
                                style={{ fontSize: '0.7em' }}
                                dominantBaseline='baseline'
                                textAnchor='middle'
                            >
                                {minimaAndMaximaValue({ reference: lineData.maximaReference, timeX, y: roundedY })}
                            </text>
                        </g>
                    );
                })}
        </>
    );
};

export default MinimaAndMaxima;
